// Ficha de Pañol — muestra herramientas del pañol, con buscador + QR bulk.
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Package, User, ArrowLeft, Loader2, Search, Printer, Download, Wrench, MapPin, History, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../../lib/printUtils';

export default function PanolDetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [panol, setPanol] = useState(null);
  const [herramientas, setHerramientas] = useState([]);
  const [stats, setStats] = useState({ total: 0, personas_count: 0, por_persona: [] });
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bodega/panoles/${id}`);
      setPanol(res.data.panol);
      setHerramientas(res.data.herramientas || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando pañol');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const filtered = herramientas.filter((h) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (h.descripcion || '').toLowerCase().includes(f)
      || (h.codigo_material || '').toLowerCase().includes(f)
      || (h.asignado_a_nombre || '').toLowerCase().includes(f);
  });

  // Carlos (10/05/2026): Imprimir y Excel del listado de herramientas del pañol
  const imprimirListado = () => {
    if (!filtered.length) { toast.warning('Sin herramientas para imprimir'); return; }
    printTable({
      title: `Pañol · ${panol?.codigo || ''} · Listado de Herramientas`,
      subtitle: `${panol?.descripcion || ''} · ${filtered.length} herramientas · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Pañol', value: `${panol?.codigo || ''} - ${panol?.descripcion || ''}` },
        { label: 'Total herramientas', value: stats.total },
        { label: 'Personas a cargo', value: stats.personas_count || 0 },
        { label: 'Pañol directo', value: stats.por_persona?.find(p => p.nombre === '(pañol directo)')?.count || 0 },
      ],
      columns: [
        { key: 'codigo_material', label: 'Código', mono: true },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cantidad', label: 'Cant.', align: 'right', mono: true },
        { key: 'asignado_a_nombre', label: 'Asignada a', format: (v) => v || '(pañol directo)' },
        { key: 'fecha_asignacion', label: 'Fecha asignación', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
        { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || 'OK').toUpperCase() },
      ],
      rows: filtered,
    });
  };

  const exportarListadoExcel = () => {
    if (!filtered.length) { toast.warning('Sin herramientas para exportar'); return; }
    exportToCSV({
      filename: `panol_${panol?.codigo || 'sin_codigo'}_herramientas`,
      title: `CHIRINO STEEL · Pañol ${panol?.codigo || ''} · ${panol?.descripcion || ''}`,
      summary: [
        { label: 'Código pañol', value: panol?.codigo || '-' },
        { label: 'Descripción pañol', value: panol?.descripcion || '-' },
        { label: 'Total herramientas', value: filtered.length },
        { label: 'Personas a cargo', value: stats.personas_count || 0 },
        { label: 'Filtro aplicado', value: filter || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'codigo_material', label: 'Código' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cantidad', label: 'Cantidad', format: (v) => Number(v) || 0 },
        { key: 'unidad', label: 'Unidad' },
        { key: 'asignado_a_nombre', label: 'Asignada a', format: (v) => v || '(pañol directo)' },
        { key: 'rut_asignado', label: 'RUT' },
        { key: 'ot_destino', label: 'OT' },
        { key: 'fecha_asignacion', label: 'Fecha asignación', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
        { key: 'estado', label: 'Estado' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: filtered,
    });
  };

  const imprimirQRDelPanol = async () => {
    if (herramientas.length === 0) { toast.error('Sin herramientas para imprimir'); return; }
    if (herramientas.length > 200) { toast.error('Máximo 200 por PDF. Filtra primero.'); return; }
    try {
      const baseUrl = window.location.origin;
      const codigos = herramientas.map(h => h.codigo_material).filter(Boolean);
      const res = await api.get('/bodega/herramientas/qr-etiquetas', {
        params: { codigos: codigos.join(','), base_url: baseUrl },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR_${panol?.nombre || 'panol'}_${codigos.length}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF con ${codigos.length} etiquetas QR`);
    } catch {
      toast.error('Error al generar PDF');
    }
  };

  if (loading) {
    return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }
  if (!panol) {
    return <div className="p-6 text-center text-muted-foreground">Pañol no encontrado</div>;
  }

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4" data-testid="page-panol-detalle">
      <Button variant="ghost" size="sm" onClick={() => navigate('/bodega/panoles')}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Volver a pañoles
      </Button>

      {/* Header */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Package className="w-6 h-6 text-indigo-600" /> {panol.nombre}
              </h1>
              <div className="text-sm text-muted-foreground mt-1 flex gap-3 flex-wrap">
                <span><Badge variant="outline">{panol.tipo}</Badge></span>
                {panol.ubicacion_geo && <span>📍 {panol.ubicacion_geo}</span>}
                {panol.ot_asociada > 0 && <span>OT {panol.ot_asociada}</span>}
                {panol.responsable_nombre && (
                  <span><User className="w-3 h-3 inline mr-1" />{panol.responsable_nombre} ({panol.responsable_tipo})</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={imprimirQRDelPanol}
                disabled={herramientas.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
                title={herramientas.length === 0 ? 'Sin herramientas asignadas a este pañol aún' : `Descargar PDF con ${herramientas.length} etiquetas QR`}
                data-testid="btn-qr-panol"
              >
                <Printer className="w-4 h-4 mr-1" /> Imprimir QR ({herramientas.length})
              </Button>
              <Button variant="outline" onClick={imprimirListado} title="Imprimir listado de herramientas" data-testid="btn-print-panol-list">
                <Printer className="w-4 h-4 mr-1" /> Imprimir Lista
              </Button>
              <Button variant="outline" onClick={exportarListadoExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-panol-list">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Total herramientas</div>
          <div className="text-3xl font-bold text-indigo-700">{stats.total}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Personas a cargo</div>
          <div className="text-3xl font-bold text-emerald-700">{stats.personas_count || 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-muted-foreground">Pañol directo</div>
          <div className="text-3xl font-bold text-amber-700">
            {stats.por_persona?.find(p => p.nombre === '(pañol directo)')?.count || 0}
          </div>
        </CardContent></Card>
      </div>

      {/* Búsqueda */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder="Buscar en este pañol…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="input-filter-panol"
        />
      </div>

      {/* Tabla de herramientas */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              {herramientas.length === 0 ? 'Este pañol aún no tiene herramientas asignadas.' : 'Sin resultados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-htas-panol">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-2 py-2">Código</th>
                    <th className="text-left px-2 py-2">Herramienta</th>
                    <th className="text-right px-2 py-2">Cant.</th>
                    <th className="text-left px-2 py-2">A cargo</th>
                    <th className="text-left px-2 py-2">Ubicación</th>
                    <th className="text-left px-2 py-2">Desde</th>
                    <th className="text-center px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((h) => (
                    <tr key={h.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50"
                      data-testid={`row-hta-${h.id}`}>
                      <td className="px-2 py-1.5 font-mono text-xs">{h.codigo_material}</td>
                      <td className="px-2 py-1.5">
                        <Wrench className="w-3 h-3 inline mr-1 text-amber-600" />
                        {h.descripcion}
                      </td>
                      <td className="px-2 py-1.5 text-right">{h.cantidad}</td>
                      <td className="px-2 py-1.5">
                        {h.asignado_a_nombre || <span className="text-muted-foreground italic">(pañol directo)</span>}
                      </td>
                      <td className="px-2 py-1.5 text-xs">
                        <MapPin className="w-3 h-3 inline text-slate-500" /> {h.ubicacion || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-muted-foreground font-mono">
                        {(h.fecha_asignacion || '').substring(0, 10)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => navigate(`/bodega/herramientas/${encodeURIComponent(h.codigo_material)}/timeline`)}
                          className="text-indigo-700 hover:bg-indigo-50 p-1 rounded"
                          title="Ver timeline / trazabilidad"
                          data-testid={`btn-timeline-${h.id}`}
                        >
                          <History className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
